import { createEffect, createSignal, For, Show, createMemo, onMount, onCleanup, type JSX } from "solid-js"
import { produce } from "solid-js/store"
import type { Session } from "@opencode-ai/sdk/v2/client"
import { useNavigate } from "@solidjs/router"
import { Binary } from "@opencode-ai/core/util/binary"
import { base64Encode } from "@opencode-ai/core/util/encode"
import { getFilename } from "@opencode-ai/core/util/path"
import { InlineInput } from "@opencode-ai/ui/inline-input"
import { useLayout } from "@/context/layout"
import { useServerSync } from "@/context/server-sync"
import { useServerSDK } from "@/context/server-sdk"
import { useLanguage } from "@/context/language"
import { errorMessage, sortedRootSessions } from "@/pages/layout/helpers"
import { SessionActionsMenu } from "@/components/session/session-actions-menu"
import { sessionTitle, isDefaultSessionTitle } from "@/utils/session-title"
import { getRelativeTime } from "@/utils/time"
import { showToast } from "@/utils/toast"
import proIconRaw from "@/assets/self-develop/pro.svg?raw"
import "@/components/zd-left-sidebar.css"

// 条目右侧的展开箭头：展开时旋转 90° 朝下，收起时朝右。
function ProjectChevron(props: { expanded: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      class="w-[14px] h-[14px] shrink-0 transition-transform"
      style={{ transform: props.expanded ? "rotate(90deg)" : "rotate(0deg)" }}
    >
      <path d="M6 4L10 8L6 12" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  )
}

// 滚动加载哨兵：哨兵元素进入视口时自动触发 onTrigger，带 loading 防重复。
// 仅在父级 <Show when={expanded()}> 内渲染，展开时挂载并启动观察，收起时自动断开。
function ScrollSentinel(props: {
  onTrigger: () => Promise<void> | void
  active: boolean
}): JSX.Element {
  let ref: HTMLDivElement | undefined
  const [loading, setLoading] = createSignal(false)

  onMount(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) return
        if (!props.active || loading()) return
        setLoading(true)
        Promise.resolve(props.onTrigger()).finally(() => setLoading(false))
      },
      { rootMargin: "200px" },
    )
    if (ref) observer.observe(ref)
    onCleanup(() => observer.disconnect())
  })

  return <div ref={ref} class="h-px w-full" aria-hidden="true" />
}

// 工作空间条目的文字样式（工作空间行与会话行共用）
const ROW_TEXT_STYLE = {
  "font-family": '"Microsoft YaHei", "Microsoft YaHei"',
  "font-weight": 400,
  "font-size": "16px",
  color: "#000000",
  "line-height": "21px",
} as const

// 工作空间下的一条会话。hover 时右侧浮出「...」菜单（重命名 / 分享… / 导出… / 归档 / 删除…），
// 相对时间让位给菜单按钮。重命中的标题就地切成行内输入框。
function ZdSessionRow(props: { session: Session; directory: string }): JSX.Element {
  const language = useLanguage()
  const navigate = useNavigate()
  const serverSDK = useServerSDK()
  const serverSync = useServerSync()
  const [, setStore] = serverSync().child(props.directory)

  const [menuOpen, setMenuOpen] = createSignal(false)
  const [editing, setEditing] = createSignal(false)
  const [nameDraft, setNameDraft] = createSignal("")
  const [saving, setSaving] = createSignal(false)
  // 菜单收起、焦点交接会带来一次性的 blur，编辑器会"刚出现就消失"。
  // 先关掉 blur 处理，下一帧再打开（同 session-sortable-terminal-tab-v2）。
  const [blurEnabled, setBlurEnabled] = createSignal(false)
  let input: HTMLInputElement | undefined
  let blurFrame: number | undefined

  const rawTitle = () => sessionTitle(props.session.title)
  const label = () => rawTitle() || "新对话"
  // 会话最后更新时间的相对描述（"刚刚"/"3分钟前"/"2天前"）。
  const updatedLabel = () =>
    getRelativeTime(new Date(props.session.time.updated ?? props.session.time.created).toISOString(), language.t)

  createEffect(() => {
    if (!editing()) {
      setBlurEnabled(false)
      return
    }
    const el = input
    if (!el) return
    el.focus()
    el.select()
    if (blurFrame !== undefined) cancelAnimationFrame(blurFrame)
    blurFrame = requestAnimationFrame(() => {
      blurFrame = undefined
      setBlurEnabled(true)
    })
  })

  onCleanup(() => {
    if (blurFrame !== undefined) cancelAnimationFrame(blurFrame)
  })

  const openRename = () => {
    setNameDraft(rawTitle() ?? "")
    setEditing(true)
  }

  const saveRename = async () => {
    const next = nameDraft().trim()
    if (saving()) return
    if (!next || next === (rawTitle() ?? "")) {
      setEditing(false)
      return
    }
    setSaving(true)
    await serverSDK()
      .api.session.rename({ sessionID: props.session.id, title: next, directory: props.directory })
      .then(() => {
        setStore(
          produce((draft) => {
            const match = Binary.search(draft.session, props.session.id, (item) => item.id)
            if (match.found) draft.session[match.index].title = next
          }),
        )
        setEditing(false)
      })
      .catch((err) =>
        showToast({
          title: language.t("common.requestFailed"),
          description: errorMessage(err, language.t("common.requestFailed")),
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <div
      class="zd-session-row"
      classList={{
        "zd-session-row--menu-open": menuOpen(),
        "zd-session-row--editing": editing(),
      }}
    >
      <Show
        when={!editing()}
        fallback={
          <InlineInput
            ref={(el: HTMLInputElement) => {
              input = el
            }}
            class="zd-session-row__input"
            value={nameDraft()}
            disabled={saving()}
            onInput={(event) => setNameDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === "Enter") {
                event.preventDefault()
                void saveRename()
                return
              }
              if (event.key === "Escape") {
                event.preventDefault()
                setEditing(false)
              }
            }}
            onBlur={() => {
              if (!blurEnabled() || saving()) return
              setEditing(false)
            }}
          />
        }
      >
        <button
          class="zd-session-row__main"
          style={ROW_TEXT_STYLE}
          onClick={(event) => {
            // 点击导航后移除焦点，避免 :focus-within 让「...」菜单在跳转后仍显示，
            // 与右侧相对时间重叠。
            event.currentTarget.blur()
            navigate(`/${base64Encode(props.directory)}/session/${props.session.id}`)
          }}
        >
          <span class="w-1 h-1 rounded-full bg-v2-text-text-tertiary shrink-0" />
          <span class="flex-1 min-w-0 truncate">{label()}</span>
          <span class="zd-conv-time">{updatedLabel()}</span>
        </button>
      </Show>
      {/* 菜单常驻渲染：编辑态下只是 CSS 隐藏。放在 <Show> 里会在重命名回调
          （菜单关闭流程中）把它卸载掉，Kobalte 的关闭/焦点恢复就会被打断。 */}
      <div class="zd-session-row__menu">
        <SessionActionsMenu
          sessionID={props.session.id}
          directory={props.directory}
          onRename={openRename}
          onOpenChange={setMenuOpen}
        />
      </div>
    </div>
  )
}

export function ZdWorkspaceList(): JSX.Element {
  const layout = useLayout()
  const serverSync = useServerSync()

  const [expandedProjects, setExpandedProjects] = createSignal<Set<string>>(new Set())

  const toggleProject = (worktree: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev)
      if (next.has(worktree)) next.delete(worktree)
      else next.add(worktree)
      return next
    })
  }

  const projectName = (project: { name?: string; worktree: string }) =>
    project.name || getFilename(project.worktree) || project.worktree

  return (
    <div class="px-2 pb-2">
      <For each={layout.projects.list()}>
        {(project) => {
          const expanded = createMemo(() => expandedProjects().has(project.worktree))
          const [store, setStore] = serverSync().child(project.worktree)
          // allRootSessions：已加载的根会话（含空会话），用于 hasMore/加载水位判断；
          // sessions：过滤掉"没有任何消息的会话"（默认标题）后的可展示会话。
          const allRootSessions = createMemo(() => sortedRootSessions(store, Date.now()))
          const sessions = createMemo(() =>
            allRootSessions().filter((s) => !isDefaultSessionTitle(s.title)),
          )
          const hasMore = createMemo(() => !!store.sessionCursor)

          // 滚动"加载更多"：按后端游标分页追加下一页（不抬高 limit，页大小恒定）。
          const loadMore = async () => {
            await serverSync().project.loadSessions(project.worktree, { append: true })
          }

          return (
            <div class="mb-1">
              <button
                class="flex items-center gap-1.5 w-full px-2 py-1.5 text-left rounded-md hover:bg-black/[0.04] transition-colors"
                style={ROW_TEXT_STYLE}
                onClick={() => toggleProject(project.worktree)}
              >
                <span class="zd-workspace-icon" innerHTML={proIconRaw} />
                <span class="truncate max-w-full">{projectName(project)}</span>
                <span class="flex-1" />
                <ProjectChevron expanded={expanded()} />
              </button>

              <Show when={expanded()}>
                <div class="ml-4 mt-0.5 mb-1">
                  <Show
                    when={sessions().length > 0}
                    fallback={
                      <div class="px-2 py-1 text-[12px] text-v2-text-text-tertiary">
                        暂无对话
                      </div>
                    }
                  >
                    <For each={sessions()}>
                      {(session) => <ZdSessionRow session={session} directory={project.worktree} />}
                    </For>
                    <Show when={hasMore()}>
                      <ScrollSentinel onTrigger={loadMore} active={hasMore()} />
                    </Show>
                  </Show>
                </div>
              </Show>
            </div>
          )
        }}
      </For>

      <Show when={layout.projects.list().length === 0}>
        <div class="px-3 py-4 text-center text-[13px] text-v2-text-text-tertiary">
          暂无工作空间
        </div>
      </Show>
    </div>
  )
}
